/**
 * Naming a planned line item after the *site*, not the investigator.
 *
 * A plan row is chosen by investigator (see InvestigatorBrowser) but every
 * downstream planning screen — site milestones, enrollment — plans the site the
 * investigator works at, so it must be labelled with that site's name. The site
 * comes from the investigator's mastered primary site (`site_name`/`site_key`);
 * when that is missing we fall back to their most recent site assignment.
 */
import type { InvestigatorProfile } from "./components/InvestigatorProfileDrawer";
import type { PlannedSite } from "./types";

/** Shown in place of a site name when the investigator has no site on record. */
export const NO_SITE_LABEL = "No site on record";

interface CurrentSite {
  name: string;
  /** Cross-trial site-profile key (golden id), when the site is mastered. */
  key?: string;
  city?: string;
  country?: string;
}

type TrialEntry = InvestigatorProfile["trials"][number];

/** The investigator's latest site assignment — open tenures first, then the
 *  most recently started. Assignments with no site name are ignored. */
function latestSiteAssignment(trials: TrialEntry[]): TrialEntry | undefined {
  return trials
    .filter((t) => t.site_name)
    .reduce<TrialEntry | undefined>((latest, t) => {
      if (!latest) return t;
      const isOpen = !t.tenure_end;
      const latestIsOpen = !latest.tenure_end;
      if (isOpen !== latestIsOpen) return isOpen ? t : latest;
      return (t.tenure_start ?? "") > (latest.tenure_start ?? "") ? t : latest;
    }, undefined);
}

/** The site this investigator currently works at, or null when none is known. */
export function currentSite(inv: InvestigatorProfile): CurrentSite | null {
  if (inv.site_name) {
    return {
      name: inv.site_name,
      key: inv.site_key ?? inv.institution_key ?? undefined,
      city: inv.site_city,
      country: inv.site_country,
    };
  }
  const assignment = latestSiteAssignment(inv.trials ?? []);
  if (assignment?.site_name) {
    return {
      name: assignment.site_name,
      key: inv.institution_key ?? undefined,
    };
  }
  return null;
}

/** Site fields of a plan line item: the site itself, with the investigator's
 *  own location standing in when the site's is unknown. */
function siteFields(
  inv: InvestigatorProfile,
): Pick<PlannedSite, "site_name" | "institution_key" | "city" | "country"> {
  const site = currentSite(inv);
  return {
    site_name: site?.name ?? "",
    institution_key: site?.key,
    city: site?.city || inv.city,
    country: site?.country || inv.country,
  };
}

/** A new plan line item for the site of the chosen investigator. */
export function plannedSiteFromInvestigator(
  inv: InvestigatorProfile,
): PlannedSite {
  return {
    id: crypto.randomUUID(),
    site_key: inv.inv_key,
    investigator_name: inv.name,
    region: inv.region ?? "",
    therapeutic_areas: inv.therapeutic_areas ?? [],
    status: "candidate",
    notes: "",
    criteria: {},
    ...siteFields(inv),
  };
}

/** Re-derive a stored line item's site from the live investigator record, so
 *  plans saved before a site was mastered (or renamed) still show it today.
 *  Left untouched when the investigator is no longer in the directory. */
export function resolvePlannedSite(
  site: PlannedSite,
  inv: InvestigatorProfile | undefined,
): PlannedSite {
  return inv ? { ...site, ...siteFields(inv) } : site;
}

/** Display name for a plan line item. */
export function siteLabel(site: PlannedSite): string {
  return site.site_name || NO_SITE_LABEL;
}

/**
 * The sites a plan is actually carrying into planning. Excluded (rejected)
 * sites stay on the plan — the list reflects the shortlist algorithm's
 * output — but drop out of enrollment forecasts, budget quantities, and
 * milestone planning. This rule lives here so every consumer agrees.
 */
export function activeSites(sites: PlannedSite[]): PlannedSite[] {
  return sites.filter((s) => s.status !== "rejected");
}
